import React, { useState } from "react";
import { X, Send, ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { parsePhoneNumber, isValidPhoneNumber } from "libphonenumber-js";

const InterviewPrepInterestForm = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    targetRole: "",
    interviewTimeline: "",
    englishConfidence: "",
    additionalInfo: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  const targetRoleOptions = [
    "Tech/Engineering",
    "Marketing/Sales",
    "HR/Operations",
    "Finance",
    "Other",
  ];

  const interviewTimelineOptions = [
    "Urgent (1-2 weeks)",
    "Soon (This month)",
    "Just preparing (3+ months)",
  ];

  const englishConfidenceOptions = [
    "I struggle to find the right words",
    "I'm okay, but I sound repetitive/basic",
    "I'm fluent, I just need interview polish",
    "Other",
  ];

  const MAX_PHONE_LENGTH = 20;
  const PHONE_ERROR_MSG =
    "Please enter a valid phone number with country code (e.g., +1 for US, +44 for UK)";

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value;
    if (!value) {
      setFormData((prev) => ({ ...prev, phone: "" }));
      setPhoneError("");
      return;
    }
    if (value.startsWith("+")) {
      value = "+" + value.slice(1).replace(/[^\d\s\-()]/g, "");
    } else {
      value = value.replace(/[^\d\s\-()+]/g, "");
      if (value && !value.startsWith("+") && /^\d/.test(value))
        value = "+" + value;
    }
    if (value.length > MAX_PHONE_LENGTH)
      value = value.slice(0, MAX_PHONE_LENGTH);
    setFormData((prev) => ({ ...prev, phone: value }));
    validatePhoneNumber(value);
  };

  const handlePhoneBlur = () => {
    if (formData.phone.trim()) validatePhoneNumber(formData.phone, true);
  };

  const validatePhoneNumber = (phoneValue, forceValidation = false) => {
    if (!phoneValue?.trim()) {
      setPhoneError("");
      return false;
    }
    if (!forceValidation && phoneValue.trim().length < 7) {
      setPhoneError("");
      return false;
    }
    try {
      if (isValidPhoneNumber(phoneValue)) {
        setPhoneError("");
        return true;
      }
      setPhoneError(PHONE_ERROR_MSG);
      return false;
    } catch {
      setPhoneError(PHONE_ERROR_MSG);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate phone number before submission
    if (!validatePhoneNumber(formData.phone, true)) {
      toast.error(PHONE_ERROR_MSG);
      return;
    }

    setIsSubmitting(true);

    try {
      // Parse and format the phone number properly
      let formattedPhone = formData.phone.trim();
      try {
        if (isValidPhoneNumber(formData.phone)) {
          const phoneNumber = parsePhoneNumber(formData.phone);
          // Format as international number
          formattedPhone = phoneNumber.formatInternational();
        }
      } catch (error) {
        // If parsing fails, use the original value
        console.warn("Could not parse phone number, using original:", error);
      }

      const submissionData = {
        fullName: formData.fullName,
        email: formData.email,
        phone: formattedPhone,
        targetRole: formData.targetRole,
        interviewTimeline: formData.interviewTimeline,
        englishConfidence: formData.englishConfidence,
        additionalInfo: formData.additionalInfo || "",
        formType: "interview-prep",
      };

      // Submit to Google Apps Script
      await fetch(
        "https://script.google.com/macros/s/AKfycbz5nVD2xmFPs7yrSpA4TqCf8VPwb1tUy4U-aFYyXBHTzOmFSnfldYlk_yShJqyC8sQahw/exec",
        {
          method: "POST",
          mode: "no-cors", 
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(submissionData),
        },
      );

      // Close form and reset data
      onClose();
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        targetRole: "",
        interviewTimeline: "",
        englishConfidence: "",
        additionalInfo: "",
      });
      setPhoneError("");

      // // Open Calendly link in new tab
      // window.open(
      //   "https://calendly.com/bammbuu-languages/career-strategy-english-audit",
      //   "_blank",
      // );

      // Redirect to interview success page
      navigate("/interview-success");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(
        "There was an error submitting the form. Please try again or contact us directly.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Form Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="relative mx-4 max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-gradient-to-br from-[#F0FDF1] to-[#E6FDE9] p-8 shadow-2xl">
            <button
              onClick={onClose}
              className="absolute right-6 top-6 flex items-center justify-center rounded-full bg-white p-2 text-gray-600 shadow-lg transition-all hover:bg-gray-50 hover:shadow-xl"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>

            {/* Header Section */}
            <div className="mb-8 text-center">
              <div className="mb-4 flex justify-center">
                <div className="rounded-full bg-[#14B82C] p-4 shadow-lg">
                  <Send className="h-8 w-8 text-white" />
                </div>
              </div>
              <h2 className="mb-4 text-4xl font-bold text-[#042F0C]">
                Free Interview Prep Assessment
              </h2>
              <p className="text-xl leading-relaxed text-[#3d3d3d]">
                We're excited to help you prepare for your interview! Just share
                a few details below, and an advisor will reach out to discuss
                how our conversation-based approach can help you achieve
                interview success.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Section 1: Contact Information */}
              <div className="rounded-2xl border border-[#14B82C]/20 bg-white p-8 shadow-lg">
                <div className="mb-6 flex items-center gap-3">
                  <div className="rounded-full bg-[#FFBF00] p-2">
                    <span className="text-lg font-bold text-[#042F0C]">1</span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#042F0C]">
                    Contact Information
                  </h3>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label className="mb-3 block text-sm font-semibold text-[#042F0C]">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      className="w-full rounded-xl border-2 border-[#14B82C]/30 px-4 py-3 text-[#042F0C] transition-all focus:border-[#14B82C] focus:outline-none focus:ring-2 focus:ring-[#14B82C]/20"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label className="mb-3 block text-sm font-semibold text-[#042F0C]">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full rounded-xl border-2 border-[#14B82C]/30 px-4 py-3 text-[#042F0C] transition-all focus:border-[#14B82C] focus:outline-none focus:ring-2 focus:ring-[#14B82C]/20"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="mb-3 block text-sm font-semibold text-[#042F0C]">
                      Phone Number/WhatsApp *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      onBlur={handlePhoneBlur}
                      required
                      className={`w-full rounded-xl border-2 px-4 py-3 text-[#042F0C] transition-all focus:outline-none focus:ring-2 ${
                        phoneError
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                          : "border-[#14B82C]/30 focus:border-[#14B82C] focus:ring-[#14B82C]/20"
                      }`}
                      placeholder="+1 555-123-4567"
                      maxLength={MAX_PHONE_LENGTH}
                    />
                    {phoneError && (
                      <p className="mt-2 text-sm text-red-600">{phoneError}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Include country code (e.g., +1 for US, +44 for UK)
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 2: Interview Preparation */}
              <div className="rounded-2xl border border-[#14B82C]/20 bg-white p-8 shadow-lg">
                <div className="mb-6 flex items-center gap-3">
                  <div className="rounded-full bg-[#FFBF00] p-2">
                    <span className="text-lg font-bold text-[#042F0C]">2</span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#042F0C]">
                    Interview Preparation
                  </h3>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label className="mb-3 block text-sm font-semibold text-[#042F0C]">
                      What is your target role? *
                    </label>
                    <div className="relative">
                      <select
                        name="targetRole"
                        value={formData.targetRole}
                        onChange={handleInputChange}
                        required
                        className="w-full appearance-none rounded-xl border-2 border-[#14B82C]/30 bg-white px-4 py-3 pr-12 text-[#042F0C] transition-all focus:border-[#14B82C] focus:outline-none focus:ring-2 focus:ring-[#14B82C]/20"
                      >
                        <option value="">Select target role</option>
                        {targetRoleOptions.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#3d3d3d]" />
                    </div>
                  </div>

                  <div>
                    <label className="mb-3 block text-sm font-semibold text-[#042F0C]">
                      When is your next potential interview? *
                    </label>
                    <div className="relative">
                      <select
                        name="interviewTimeline"
                        value={formData.interviewTimeline}
                        onChange={handleInputChange}
                        required
                        className="w-full appearance-none rounded-xl border-2 border-[#14B82C]/30 bg-white px-4 py-3 pr-12 text-[#042F0C] transition-all focus:border-[#14B82C] focus:outline-none focus:ring-2 focus:ring-[#14B82C]/20"
                      >
                        <option value="">Select timeline</option>
                        {interviewTimelineOptions.map((timeline) => (
                          <option key={timeline} value={timeline}>
                            {timeline}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#3d3d3d]" />
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="mb-3 block text-sm font-semibold text-[#042F0C]">
                      How confident are you in your English right now? *
                    </label>
                    <div className="relative">
                      <select
                        name="englishConfidence"
                        value={formData.englishConfidence}
                        onChange={handleInputChange}
                        required
                        className="w-full appearance-none rounded-xl border-2 border-[#14B82C]/30 bg-white px-4 py-3 pr-12 text-[#042F0C] transition-all focus:border-[#14B82C] focus:outline-none focus:ring-2 focus:ring-[#14B82C]/20"
                      >
                        <option value="">Select confidence level</option>
                        {englishConfidenceOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#3d3d3d]" />
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <label className="mb-3 block text-sm font-semibold text-[#042F0C]">
                    Please share any other relevant information to your language
                    learning or interview preparation journey:
                  </label>
                  <textarea
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full resize-none rounded-xl border-2 border-[#14B82C]/30 px-4 py-3 text-[#042F0C] transition-all focus:border-[#14B82C] focus:outline-none focus:ring-2 focus:ring-[#14B82C]/20"
                    placeholder="Optional - Share any additional details about your interview goals, specific challenges, or what you hope to achieve..."
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`flex transform items-center gap-3 rounded-full px-12 py-4 text-xl font-bold transition-all hover:scale-105 ${
                    isSubmitting
                      ? "cursor-not-allowed bg-gray-400 text-gray-600"
                      : "bg-gradient-to-r from-[#14B82C] to-[#0F9A1A] text-white shadow-lg hover:from-[#0F9A1A] hover:to-[#0D7A15] hover:shadow-xl"
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="h-6 w-6" />
                      Submit
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default InterviewPrepInterestForm;
